/**
 * Venom 2.0 MMY routes
 *  - /make/model/newyear
 *  - /make/model/usedyear
 *  - /make/model/newyear/submodel
 *  - /make/model/usedyear/submodel
 *  - /make/model/
 *
 *  more about dynamic routes on https://gitlab.com/edmundscom/edmunds/node-site-venom/-/wikis/Routing-in-Venom#venom-20-routes
 */
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { getModelState } from 'client/data/luckdragon/model';
import { UrlModel } from 'client/data/models/url';
import { buildVehiclePath, VehicleGraphQLModel } from 'client/data/models/vehicle-graphql';
import { makes, TWO_WORDS_MAKE_NAMES_MAP } from 'client/data/constants/active-makes';
import { getQuery } from 'client/utils/location';
import { isUsedCoreMmyYear } from 'site-modules/shared/utils/core-page/used-core-mmy';
import { create404Error } from 'site-modules/shared/utils/errors';
import { isUsed } from 'site-modules/shared/utils/publication-states';
import { getLoadablePage } from 'site-modules/shared/components/loadable-page/loadable-page';

export const routes = [
  /**
   * New: new-core
   * Used: srp(<2022), new-core(>=2022)
   * [0]
   */
  {
    path: [`/:make(${makes.join('|')})/:model/:year`, `/:make(${makes.join('|')})/:model/:year/used`],
    exact: true,
    preload: (params, store) => {
      const preloader = new ModelPreloader(store);
      preloader.resolve(buildVehiclePath(params), VehicleGraphQLModel);
      return preloader.load();
    },
    page: getLoadablePage((params, store) => {
      const modelState = getModelState(store.getState());
      const vehicleSubmodel = modelState.get(buildVehiclePath(params), VehicleGraphQLModel);

      if (!vehicleSubmodel) {
        throw create404Error('Page not found');
      }

      const isUsedPubState = isUsed(vehicleSubmodel.pubStates);
      const pageDefinitionParams = {
        ...params,
        make: TWO_WORDS_MAKE_NAMES_MAP[params.make] || params.make,
      };

      if (isUsedPubState && !isUsedCoreMmyYear(params.year)) {
        return import(/* webpackChunkName: "used_srp_core" */ '../../inventory/pages/usurp/usurp-core-definition').then(
          page => page.default({ ...pageDefinitionParams, isSrpOnUsedCore: true })
        );
      }

      return import(/* webpackChunkName: "new_model_core" */ '../../core-page/pages/new-core/new-core-definition').then(
        page => page.default(isUsedPubState ? { ...pageDefinitionParams, isUsed: true } : pageDefinitionParams)
      );
    }),
    resolveLocationInParallelToPagePreload: true,
  },

  /**
   * New: model-price-checker
   * Used: srp
   * [1]
   */
  {
    path: `/:make(${makes.join('|')})/:model/:year/msrp`,
    exact: true,
    preload: (params, store) => {
      const preloader = new ModelPreloader(store);
      preloader.resolve(buildVehiclePath(params), VehicleGraphQLModel);

      return preloader.load();
    },
    page: getLoadablePage((params, store) => {
      const modelState = getModelState(store.getState());
      const vehicle = modelState.get(buildVehiclePath(params), VehicleGraphQLModel);

      if (!vehicle) {
        throw create404Error('Page not found');
      }
      const pageDefinitionParams = {
        ...params,
        make: TWO_WORDS_MAKE_NAMES_MAP[params.make] || params.make,
      };

      if (isUsed(vehicle.pubStates)) {
        return import(/* webpackChunkName: "used_srp_core" */ '../../inventory/pages/usurp/usurp-core-definition').then(
          page => page.default({ ...pageDefinitionParams, isSrpOnUsedCore: true })
        );
      }

      const hasUtmParam = Object.keys(getQuery(store.getState().pageContext?.location)).some(
        key => key === 'utm_campaign'
      );

      if (hasUtmParam) {
        return import(
          /* webpackChunkName: "model-price-checker-social" */ '../../price-checker/pages/model-price-checker-social/model-price-checker-social-definition'
        );
      }

      return import(
        /* webpackChunkName: "model-price-checker" */ '../../price-checker/pages/model-price-checker/model-price-checker-definition'
      );
    }),
    resolveLocationInParallelToPagePreload: true,
  },

  /**
   * New: new-core
   * Used: new-core
   * [2]
   */
  {
    path: [`/:make(${makes.join('|')})/:model/:year/review`, `/:make(${makes.join('|')})/:model/:year/used/review`],
    exact: true,
    preload: (params, store) => {
      const preloader = new ModelPreloader(store);
      preloader.resolve(buildVehiclePath(params), VehicleGraphQLModel);

      return preloader.load();
    },
    page: getLoadablePage((params, store) => {
      const modelState = getModelState(store.getState());
      const vehicle = modelState.get(buildVehiclePath(params), VehicleGraphQLModel);

      if (!vehicle) {
        throw create404Error('Page not found');
      }

      const pageDefinitionParams = {
        ...params,
        make: TWO_WORDS_MAKE_NAMES_MAP[params.make] || params.make,
      };

      if (isUsed(vehicle.pubStates)) {
        return import(
          /* webpackChunkName: "new_model_core" */ '../../core-page/pages/new-core/new-core-definition'
        ).then(page => page.default({ ...pageDefinitionParams, isUsed: true }));
      }

      return import(/* webpackChunkName: "new_model_core" */ '../../core-page/pages/new-core/new-core-definition').then(
        page => page.default(pageDefinitionParams)
      );
    }),
    resolveLocationInParallelToPagePreload: true,
  },

  {
    path: [
      `/:make(${makes.join('|')})/:model/:year/:submodel`,
      `/:make(${makes.join('|')})/:model/:year/:submodel/used`,
    ],
    exact: true,
    shouldCallParseApi: true,
    preload: (params, store) => {
      const preloader = new ModelPreloader(store);
      preloader.resolve(buildVehiclePath(params), VehicleGraphQLModel);

      return preloader.load();
    },
    page: getLoadablePage((params, store) => {
      const modelState = getModelState(store.getState());
      const vehicle = modelState.get(buildVehiclePath(params), VehicleGraphQLModel);

      if (!vehicle) {
        throw create404Error('Page not found');
      }

      const pageDefinitionParams = {
        ...params,
        make: TWO_WORDS_MAKE_NAMES_MAP[params.make] || params.make,
      };

      if (isUsed(vehicle.pubStates)) {
        const isValidSrpUrl = modelState.get('context.isValidUrl', UrlModel);
        const isSupportedFilter = modelState.get('context.attributes.inventory.isSupportedFilter', UrlModel);
        // api/url/v3/parse publication state is cached for up to 1 week, so we need to check that it matches
        const publicationStateFromParseApi = modelState.get('context.vehicle.publicationState', UrlModel);

        if (isValidSrpUrl && isSupportedFilter && publicationStateFromParseApi === 'used') {
          return import(
            /* webpackChunkName: "used_srp_core" */ '../../inventory/pages/usurp/usurp-core-definition'
          ).then(page => page.default({ ...pageDefinitionParams, isSrpOnUsedCore: false }));
        }

        return import(
          /* webpackChunkName: "new_model_core" */ '../../core-page/pages/new-core/new-core-definition'
        ).then(page => page.default({ ...pageDefinitionParams, isUsed: true }));
      }

      return import(/* webpackChunkName: "new_model_core" */ '../../core-page/pages/new-core/new-core-definition').then(
        page => page.default(pageDefinitionParams)
      );
    }),
    resolveLocationInParallelToPagePreload: true,
  },

  /**
   * without year
   * New: new-core
   * Used: srp
   * [3]
   */
  {
    path: `/:make(${makes.join('|')})/:model`,
    exact: true,
    preload: (params, store) => {
      const preloader = new ModelPreloader(store);
      preloader.resolve(buildVehiclePath(params), VehicleGraphQLModel);
      return preloader.load();
    },
    page: getLoadablePage((params, store) => {
      const modelState = getModelState(store.getState());
      const vehicleSubmodel = modelState.get(buildVehiclePath(params), VehicleGraphQLModel);

      if (!vehicleSubmodel) {
        throw create404Error('Page not found');
      }

      const pageDefinitionParams = {
        ...params,
        make: TWO_WORDS_MAKE_NAMES_MAP[params.make] || params.make,
      };

      if (isUsed(vehicleSubmodel.pubStates)) {
        return import(/* webpackChunkName: "used_srp_core" */ '../../inventory/pages/usurp/usurp-core-definition').then(
          page =>
            page.default({
              ...pageDefinitionParams,
              year: vehicleSubmodel?.year,
              isSrpOnUsedCore: true,
            })
        );
      }

      return import(/* webpackChunkName: "new_model_core" */ '../../core-page/pages/new-core/new-core-definition').then(
        page => page.default(pageDefinitionParams)
      );
    }),
    resolveLocationInParallelToPagePreload: true,
    enableLooseEtag: true,
  },
];
